import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileText, Users, Package, TrendingUp, Plus, Eye, Euro, Percent, BarChart3 } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';

const DashboardPage = () => {
  const { user } = useAuth();
  const isSlave = user?.role === 'company_user';
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, monthlyRes] = await Promise.all([
          dashboardApi.getStats(),
          dashboardApi.getMonthlyStats(12),
        ]);
        setStats(statsRes.data);
        setMonthly(monthlyRes.data?.months || []);
      } catch (error) {
        toast.error('Errore nel caricamento delle statistiche');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">Dashboard</h1>
          <p className="text-muted-foreground text-sm">Panoramica della tua attività</p>
        </div>
        <Link to="/ddt/new">
          <Button data-testid="new-ddt-btn" className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            NUOVO DDT
          </Button>
        </Link>
      </div>

      {/* Stats Grid - Bento Style */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${isSlave ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-6`}>
        <Card className="card-swiss">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Fatturato Totale
            </CardTitle>
            <Euro className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div data-testid="total-revenue" className="text-2xl font-bold font-['JetBrains_Mono']">
              {formatCurrency(stats?.total_revenue)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Questo mese: {formatCurrency(stats?.monthly_revenue)}
            </p>
          </CardContent>
        </Card>

        {!isSlave && (
        <Card className="card-swiss bg-green-50/50 border-green-200/50">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-green-700 uppercase tracking-wider">
              Guadagno Totale
            </CardTitle>
            <TrendingUp className="w-4 h-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div data-testid="total-margin" className="text-2xl font-bold font-['JetBrains_Mono'] text-green-700">
              {formatCurrency(stats?.total_margin)}
            </div>
            <p className="text-xs text-green-600 mt-1">
              Questo mese: {formatCurrency(stats?.monthly_margin)}
            </p>
          </CardContent>
        </Card>
        )}

        <Card className="card-swiss">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              DDT Emessi
            </CardTitle>
            <FileText className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div data-testid="total-ddt" className="text-2xl font-bold font-['JetBrains_Mono']">
              {stats?.total_ddt || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Documenti di trasporto</p>
          </CardContent>
        </Card>

        <Card className="card-swiss">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Clienti / Prodotti
            </CardTitle>
            <Users className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div data-testid="total-clients" className="text-2xl font-bold font-['JetBrains_Mono']">
              {stats?.total_clients || 0} / {stats?.total_products || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Clienti e prodotti registrati</p>
          </CardContent>
        </Card>
      </div>

      {/* Grafico mensile — Entrate/Uscite + linea Guadagni (ultimi 12 mesi) */}
      <Card className="card-swiss" data-testid="monthly-chart-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 font-['Manrope']">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Andamento mensile — ultimi 12 mesi
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            <span className="inline-block w-2.5 h-2.5 bg-emerald-500 rounded-sm align-middle mr-1"></span> Entrate DDT
            <span className="inline-block w-2.5 h-2.5 bg-rose-500 rounded-sm align-middle ml-3 mr-1"></span> Uscite
            {!isSlave && (
              <><span className="inline-block w-2.5 h-2.5 bg-blue-600 rounded-sm align-middle ml-3 mr-1"></span> Guadagno</>
            )}
          </p>
        </CardHeader>
        <CardContent>
          {monthly.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Nessun dato disponibile</div>
          ) : (
            <div className="w-full" style={{ height: 320 }} data-testid="monthly-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthly} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `€${Math.round(v / 1000)}k`}
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0),
                      name,
                    ]}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="entrate" name="Entrate" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="uscite" name="Uscite" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                  {!isSlave && (
                    <Line
                      type="monotone"
                      dataKey="guadagni"
                      name="Guadagno"
                      stroke="#2563eb"
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: '#2563eb' }}
                      activeDot={{ r: 6 }}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
